import React, { useState, useEffect, useRef } from 'react';
import { AlertTriangle, ExternalLink, Play, RefreshCw, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface ArtifactPreviewProps {
  content: string;
  type: string;
  title: string;
}

export const ArtifactPreview: React.FC<ArtifactPreviewProps> = ({ content, type, title }) => {
  // Auto-run preview for React components and JavaScript, manual for others
  const isReactComponent = ['react', 'jsx', 'tsx'].includes(type.toLowerCase());
  const isJavaScript = ['javascript', 'js'].includes(type.toLowerCase());
  const shouldAutoPreview = isReactComponent || isJavaScript;
  
  const [isPreviewMode, setIsPreviewMode] = useState(shouldAutoPreview);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Auto-enable preview mode when switching to React components or JavaScript
  useEffect(() => {
    if (shouldAutoPreview) {
      setIsPreviewMode(true);
      setPreviewError(null);
    }
  }, [shouldAutoPreview, content]);

  // Check if this is a web-based artifact that can be previewed
  // Note: Only browser-executable code can be previewed, not server-side code
  const isPreviewable = ['html', 'react', 'jsx', 'tsx', 'js'].includes(type.toLowerCase());
  
  // JavaScript is tricky - could be browser or Node.js code
  // For now, we'll show preview for JS but it may error for server code
  const isBrowserJavaScript = type.toLowerCase() === 'javascript' && 
    !content.includes('require(') && 
    !content.includes('module.exports') &&
    !content.includes('app.listen(') &&
    !content.includes('express') &&
    !content.includes('fs.') &&
    !content.includes('path.');
    
  const canPreview = isPreviewable || isBrowserJavaScript;

  const generatePreviewHtml = () => {
    // Base HTML template with error handling
    const baseHtml = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title}</title>
  <!-- Include Tailwind CSS for proper styling -->
  <script src="https://cdn.tailwindcss.com"></script>
  <script>
    tailwind.config = {
      theme: {
        extend: {
          colors: {
            border: "hsl(220 13% 91%)",
            input: "hsl(220 13% 91%)",
            ring: "hsl(221.2 83.2% 53.3%)",
            background: "hsl(0 0% 100%)",
            foreground: "hsl(222.2 84% 4.9%)",
            primary: {
              DEFAULT: "hsl(221.2 83.2% 53.3%)",
              foreground: "hsl(210 40% 98%)"
            },
            secondary: {
              DEFAULT: "hsl(210 40% 96%)",
              foreground: "hsl(222.2 84% 4.9%)"
            },
            destructive: {
              DEFAULT: "hsl(0 84.2% 60.2%)",
              foreground: "hsl(210 40% 98%)"
            },
            muted: {
              DEFAULT: "hsl(210 40% 96%)",
              foreground: "hsl(215.4 16.3% 46.9%)"
            },
            accent: {
              DEFAULT: "hsl(210 40% 96%)",
              foreground: "hsl(222.2 84% 4.9%)"
            },
            popover: {
              DEFAULT: "hsl(0 0% 100%)",
              foreground: "hsl(222.2 84% 4.9%)"
            },
            card: {
              DEFAULT: "hsl(0 0% 100%)",
              foreground: "hsl(222.2 84% 4.9%)"
            },
            warning: "hsl(38 92% 50%)",
            moss: "hsl(142 71% 45%)"
          }
        }
      }
    }
  </script>
  <style>
    body {
      margin: 0;
      padding: 16px;
      font-family: system-ui, -apple-system, sans-serif;
      background: white;
    }
    .error {
      color: red;
      font-family: monospace;
      white-space: pre-wrap;
      background: #ffebee;
      padding: 16px;
      border-radius: 4px;
      margin: 16px 0;
    }
    .code-display {
      background: #f8f9fa;
      border: 1px solid #e9ecef;
      border-radius: 6px;
      padding: 16px;
      margin: 16px 0;
      font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
      font-size: 14px;
      line-height: 1.5;
      overflow-x: auto;
      white-space: pre;
    }
    .code-header {
      background: #f1f3f4;
      padding: 8px 16px;
      border-bottom: 1px solid #e9ecef;
      font-weight: 600;
      font-size: 12px;
      color: #5f6368;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    /* Syntax highlighting styles for React/JSX */
    .token.comment,
    .token.prolog,
    .token.doctype,
    .token.cdata {
      color: #6a737d;
    }
    .token.punctuation {
      color: #393a34;
    }
    .token.property,
    .token.tag,
    .token.boolean,
    .token.number,
    .token.constant,
    .token.symbol,
    .token.deleted {
      color: #d73a49;
    }
    .token.selector,
    .token.attr-name,
    .token.string,
    .token.char,
    .token.builtin,
    .token.inserted {
      color: #032f62;
    }
    .token.operator,
    .token.entity,
    .token.url,
    .language-css .token.string,
    .style .token.string {
      color: #a626a4;
    }
    .token.atrule,
    .token.attr-value,
    .token.keyword {
      color: #0184bc;
    }
    .token.function,
    .token.class-name {
      color: #6f42c1;
    }
    .token.regex,
    .token.important,
    .token.variable {
      color: #e36209;
    }
  </style>
  
  <!-- Utility Libraries Available for All Artifact Types -->
  <script src="https://unpkg.com/recharts@2.8.0/umd/Recharts.js"></script>
  <script src="https://unpkg.com/dayjs@1.11.10/dayjs.min.js"></script>
  <script src="https://unpkg.com/lodash@4.17.21/lodash.min.js"></script>
  <script src="https://unpkg.com/date-fns@2.30.0/index.umd.min.js"></script>
  <script src="https://unpkg.com/uuid@9.0.1/dist/umd/uuid.min.js"></script>
  <script src="https://unpkg.com/classnames@2.3.2/index.js"></script>
  
  <!-- Setup Global Libraries for ALL Artifact Types -->
  <script>
    // Secure data fetching with allowlist
    const ALLOWED_DOMAINS = [
      // Government & Official Data Sources
      'data.gov',
      'api.census.gov',
      'opendata.gov.uk',
      'data.europa.eu',
      'data.gov.au',
      'open.canada.ca',
      
      // Financial & Economic Data
      'api.worldbank.org',
      'api.imf.org',
      'api.fred.stlouisfed.org',
      'api.nasdaq.com',
      'query1.finance.yahoo.com',
      'api.polygon.io',
      
      // APIs & Data Services
      'api.github.com',
      'jsonplaceholder.typicode.com',
      'httpbin.org',
      'api.openweathermap.org',
      'restcountries.com',
      'api.exchangerate-api.com',
      
      // CDN & Static Files
      'cdn.jsdelivr.net',
      'unpkg.com',
      'raw.githubusercontent.com',
      'gist.githubusercontent.com',
      
      // Academic & Research
      'api.crossref.org',
      'export.arxiv.org',
      'api.springer.com',
      
      // Common CSV/Data Hosts
      'docs.google.com',
      'sheets.googleapis.com',
      'drive.google.com',
      'onedrive.live.com',
      'dl.dropboxusercontent.com'
    ];
    
    // Enhanced fetch with allowlist security
    window.secureFetch = async function(url, options = {}) {
      try {
        const urlObj = new URL(url);
        const domain = urlObj.hostname.toLowerCase();
        
        // Check if domain is in allowlist (including subdomains)
        const isAllowed = ALLOWED_DOMAINS.some(allowedDomain => 
          domain === allowedDomain || domain.endsWith('.' + allowedDomain)
        );
        
        if (!isAllowed) {
          throw new Error(\`🚫 External data access denied for '\${domain}'.\\n\\n\` +
            \`For security reasons, artifacts can only fetch data from approved sources.\\n\` +
            \`To request access to '\${domain}', please contact support@aitana.chat\\n\\n\` +
            \`Currently allowed sources include:\\n\` +
            \`• Government data: data.gov, opendata.gov.uk\\n\` +
            \`• Financial APIs: api.worldbank.org, query1.finance.yahoo.com\\n\` +
            \`• Development APIs: api.github.com, jsonplaceholder.typicode.com\\n\` +
            \`• Google Sheets: docs.google.com, sheets.googleapis.com\\n\` +
            \`• And many more trusted sources\`);
        }
        
        
        // Add CORS headers for better compatibility
        const fetchOptions = {
          ...options,
          headers: {
            'Accept': 'application/json, text/csv, application/xml, text/plain, */*',
            ...options.headers
          }
        };
        
        const response = await fetch(url, fetchOptions);
        
        if (!response.ok) {
          throw new Error(\`HTTP \${response.status}: \${response.statusText}\`);
        }
        
        return response;
        
      } catch (error) {
        console.error('secureFetch error:', error.message);
        throw error;
      }
    };
    
    // Convenience methods for common data formats
    window.fetchJSON = async function(url) {
      const response = await secureFetch(url);
      return await response.json();
    };
    
    window.fetchCSV = async function(url) {
      const response = await secureFetch(url);
      const text = await response.text();
      
      // Simple CSV parser (for basic use cases)
      const lines = text.trim().split('\\n');
      const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
      const rows = lines.slice(1).map(line => {
        const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
        const obj = {};
        headers.forEach((header, index) => {
          obj[header] = values[index] || '';
        });
        return obj;
      });
      
      return { headers, rows, raw: text };
    };
    
    window.fetchXML = async function(url) {
      const response = await secureFetch(url);
      const text = await response.text();
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(text, 'text/xml');
      return { document: xmlDoc, raw: text };
    };
    
    window.fetchText = async function(url) {
      const response = await secureFetch(url);
      return await response.text();
    };
    
    // Wait for libraries to load and set up globals
    function setupGlobalLibraries() {
      // Chart components (available for all artifact types)
      if (window.Recharts) {
        const {
          LineChart, BarChart, PieChart, AreaChart, ScatterChart,
          XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
          Line, Bar, Area, Pie, Cell, Scatter
        } = window.Recharts;
        
        window.Chart = {
          LineChart, BarChart, PieChart, AreaChart, ScatterChart,
          XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
          Line, Bar, Area, Pie, Cell, Scatter
        };
        
      }
      
      // Utility functions (available for all)
      window._ = window._;
      window.uuid = window.uuid;
      window.dayjs = window.dayjs;
      window.dateFns = window.dateFns;
      window.classNames = window.classNames;
      
    }
    
    // Setup on multiple events to ensure loading
    setupGlobalLibraries();
    document.addEventListener('DOMContentLoaded', setupGlobalLibraries);
    window.addEventListener('load', setupGlobalLibraries);
  </script>
  
  <!-- Include Prism.js for syntax highlighting -->
  <script src="https://unpkg.com/prismjs@1.29.0/components/prism-core.min.js"></script>
  <script src="https://unpkg.com/prismjs@1.29.0/plugins/autoloader/prism-autoloader.min.js"></script>
  
  ${type === 'react' || type === 'jsx' || type === 'tsx' ? `
  <!-- React CDN for preview -->
  <script crossorigin src="https://unpkg.com/react@18/umd/react.production.min.js"></script>
  <script crossorigin src="https://unpkg.com/react-dom@18/umd/react-dom.production.min.js"></script>
  <script src="https://unpkg.com/@babel/standalone/babel.min.js"></script>
  
  
  <!-- React-Specific Libraries -->
  <script src="https://unpkg.com/framer-motion@10.16.5/dist/framer-motion.umd.js"></script>
  <script src="https://unpkg.com/react-hook-form@7.47.0/dist/index.umd.js"></script>
  <script src="https://unpkg.com/react-beautiful-dnd@13.1.1/dist/react-beautiful-dnd.min.js"></script>
  
  <!-- Setup global aliases for easier access -->
  <script>
    // Wait for all libraries to load before setting up aliases
    function setupGlobals() {
      // Chart components
      if (window.Recharts) {
        window.Charts = window.Recharts;
        
        // Common chart components for easy access
        const {
          LineChart, BarChart, PieChart, AreaChart, ScatterChart,
          XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
          Line, Bar, Area, Pie, Cell, Scatter
        } = window.Recharts;
        
        window.Chart = {
          LineChart, BarChart, PieChart, AreaChart, ScatterChart,
          XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
          Line, Bar, Area, Pie, Cell, Scatter
        };
        
      } else {
        console.warn('Recharts library not loaded');
      }
      
      // Date utilities
      window.dayjs = window.dayjs;
      window.dateFns = window.dateFns;
      
      // Utility functions
      window._ = window._;
      window.uuid = window.uuid;
      window.classNames = window.classNames;
      
      // Animation library
      if (window.FramerMotion) {
        window.motion = window.FramerMotion.motion;
        window.AnimatePresence = window.FramerMotion.AnimatePresence;
      }
      
      // Form library
      if (window.ReactHookForm) {
        window.useForm = window.ReactHookForm.useForm;
        window.Controller = window.ReactHookForm.Controller;
      }
      
      // Drag and Drop library
      if (window.ReactBeautifulDnd) {
        window.DragDropContext = window.ReactBeautifulDnd.DragDropContext;
        window.Droppable = window.ReactBeautifulDnd.Droppable;
        window.Draggable = window.ReactBeautifulDnd.Draggable;
      }
    }
    
    // Try to setup immediately
    setupGlobals();
    
    // Also setup when DOM is ready
    document.addEventListener('DOMContentLoaded', setupGlobals);
    
    // And setup when window loads (fallback)
    window.addEventListener('load', setupGlobals);
  </script>
  ` : ''}
</head>
<body>
  <div id="root"></div>
  
  <script>
    // Error handling
    window.addEventListener('error', function(event) {
      const errorDiv = document.createElement('div');
      errorDiv.className = 'error';
      errorDiv.textContent = 'Error: ' + event.error.message + '\\n' + event.error.stack;
      document.body.appendChild(errorDiv);
    });
    
    try {
      ${type === 'html' ? `
        // For HTML artifacts, inject the content directly
        document.getElementById('root').innerHTML = ${JSON.stringify(content)};
      ` : ''}
      
      ${(isBrowserJavaScript || type.toLowerCase() === 'js') ? `
        
        // Add execution output section FIRST - this is what users want to see
        const outputContainer = document.createElement('div');
        outputContainer.innerHTML = \`
          <div class="code-header">Execution Output</div>
          <div id="js-output" class="code-display" style="background: #f0f7ff; min-height: 40px;">
            <div style="color: #666; font-style: italic;">Code executed - check console for output</div>
          </div>
        \`;
        document.getElementById('root').appendChild(outputContainer);
        
        // Add JavaScript code section SECOND - for reference if needed
        const codeContainer = document.createElement('div');
        codeContainer.innerHTML = \`
          <div class="code-header">JavaScript Code</div>
          <pre class="code-display"><code id="js-code" class="language-javascript">\${${JSON.stringify(content)}.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</code></pre>
        \`;
        document.getElementById('root').appendChild(codeContainer);
        
        // Apply syntax highlighting after adding to DOM
        if (typeof Prism !== 'undefined') {
          Prism.highlightElement(document.getElementById('js-code'));
        }
        
        // Capture console output
        const outputDiv = document.getElementById('js-output');
        const originalLog = console.log;
        const logs = [];
        
        console.log = function(...args) {
          logs.push(args.map(arg => typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)).join(' '));
          outputDiv.innerHTML = logs.join('\\n') || '<div style="color: #666; font-style: italic;">No console output</div>';
          originalLog.apply(console, args);
        };
        
        // Execute the JavaScript code
        (function() {
          // Simple module system for basic imports
          const modules = {};
          const exports = {};
          const module = { exports };
          
          try {
            ${content}
          } catch (e) {
            console.log('Error: ' + e.message);
            // If the code has imports that can't be resolved, show the error
            if (e.message.includes('import') || e.message.includes('export')) {
              console.log('This JavaScript uses ES6 modules. For full functionality, run in a proper module environment.');
            }
          }
        })();
      ` : ''}
      
      ${isReactComponent ? `
        // For React components, show only the interactive preview
        // Code is visible in the edit pane, so we focus on the rendered output
        
        // For React artifacts, transpile and render
        const code = ${JSON.stringify(content)};
        
        try {
          // Transform JSX to JavaScript
          const transformedCode = Babel.transform(code, {
            presets: ['react'],
            plugins: []
          }).code;
          
          // Ensure Chart library is available before executing
          if (code.includes('Chart.') && !window.Chart) {
            console.warn('Chart library not ready, waiting...');
            setTimeout(() => {
              if (window.Chart) {
                console.log('Chart library now available, executing code...');
                executeReactCode();
              } else {
                console.error('Chart library failed to load');
                showChartError();
              }
            }, 1000);
            return;
          }
          
          function executeReactCode() {
            // Create a function to execute the transformed code with all available globals
            const globals = [
              'React', 'ReactDOM', 'document', 'Chart', 'dayjs', '_', 'uuid', 'classNames',
              'motion', 'AnimatePresence', 'useForm', 'Controller',
              'DragDropContext', 'Droppable', 'Draggable'
            ];
            
            const globalValues = [
              React, ReactDOM, document, window.Chart, window.dayjs, window._, window.uuid, window.classNames,
              window.motion, window.AnimatePresence, window.useForm, window.Controller,
              window.DragDropContext, window.Droppable, window.Draggable
            ];
            
            const func = new Function(...globals, transformedCode + '; return typeof App !== "undefined" ? App : null;');
            
            // Execute the code with all globals in scope
            const Component = func(...globalValues);
            
            // If we got a component, render it to the output container
            if (Component) {
              ReactDOM.render(React.createElement(Component), document.getElementById('root'));
            } else {
              // Try to execute the code directly (for cases where it renders directly)
              const directFunc = new Function(...globals, transformedCode);
              directFunc(...globalValues);
            }
          }
          
          function showChartError() {
            const errorDiv = document.createElement('div');
            errorDiv.style.cssText = 'color: orange; font-family: monospace; padding: 16px; background: #fff3cd; border-radius: 4px; margin: 16px 0; border: 1px solid #ffeaa7;';
            errorDiv.innerHTML = '<strong>Chart Library Loading Issue:</strong><br/>The Chart library is taking longer than expected to load. Try refreshing the preview.';
            document.getElementById('root').appendChild(errorDiv);
          }
          
          // Execute immediately if Chart is available or not needed
          executeReactCode();
        } catch (error) {
          const errorDiv = document.createElement('div');
          errorDiv.style.cssText = 'color: red; font-family: monospace; padding: 16px; background: #ffebee; border-radius: 4px; margin: 16px 0;';
          errorDiv.textContent = 'React Error: ' + error.message;
          document.getElementById('root').appendChild(errorDiv);
        }
      ` : ''}
    } catch (error) {
      const errorDiv = document.createElement('div');
      errorDiv.className = 'error';
      errorDiv.textContent = 'Execution Error: ' + error.message + '\\n' + error.stack;
      document.body.appendChild(errorDiv);
    }
  </script>
</body>
</html>`;

    return baseHtml;
  };

  const handlePreview = () => {
    if (!isPreviewMode) {
      setPreviewError(null);
      setIsPreviewMode(true);
    } else {
      setIsPreviewMode(false);
    }
  };

  const handleRefresh = () => {
    if (iframeRef.current) {
      iframeRef.current.src = 'about:blank';
      setTimeout(() => {
        if (iframeRef.current) {
          const html = generatePreviewHtml();
          iframeRef.current.srcdoc = html;
        }
      }, 100);
    }
  };

  const handleOpenInNewTab = () => {
    const html = generatePreviewHtml();
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    
    // Clean up the URL after a short delay
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  if (!canPreview) {
    // Show a helpful message for server-side code
    if (type.toLowerCase() === 'javascript' && (
      content.includes('require(') || 
      content.includes('app.listen(') ||
      content.includes('express') ||
      content.includes('module.exports')
    )) {
      return (
        <Alert className="mt-4 bg-blue-50 border-blue-200">
          <AlertTriangle className="h-4 w-4 text-blue-600" />
          <AlertTitle>Server-side Code</AlertTitle>
          <AlertDescription>
            This appears to be Node.js/Express server code that requires a server environment to run. 
            Download the file and run it locally with <code>node filename.js</code> after installing dependencies.
          </AlertDescription>
        </Alert>
      );
    }
    
    return null;
  }

  return (
    <TooltipProvider>
      <div className="mt-4 space-y-4">
        {/* Preview controls */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePreview}
            className="gap-2"
          >
            <Play className="h-4 w-4" />
            {isPreviewMode ? 'Hide Preview' : (isReactComponent ? 'Run Component' : 'Preview')}
          </Button>
          
          {/* Info tooltip for React components */}
          {isReactComponent && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" className="p-1">
                  <Info className="h-4 w-4 text-muted-foreground" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>This React component runs in a sandboxed iframe with React 18. Complex components may need additional dependencies.</p>
              </TooltipContent>
            </Tooltip>
          )}
          
          {isPreviewMode && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                className="gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleOpenInNewTab}
                className="gap-2"
              >
                <ExternalLink className="h-4 w-4" />
                Open in New Tab
              </Button>
            </>
          )}
        </div>

        {/* Preview iframe */}
        {isPreviewMode && (
          <div className="border rounded-lg overflow-hidden bg-white">
            <iframe
              ref={iframeRef}
              srcDoc={generatePreviewHtml()}
              className="w-full h-[600px]"
              sandbox="allow-scripts allow-same-origin"
              title={`Preview of ${title}`}
              onError={(e) => setPreviewError('Failed to load preview')}
            />
          </div>
        )}

        {/* Error display */}
        {previewError && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Preview Error</AlertTitle>
            <AlertDescription>{previewError}</AlertDescription>
          </Alert>
        )}
      </div>
    </TooltipProvider>
  );
};

export default ArtifactPreview;